// "Print X labels" links from restock, receiving and product creation carry
// receipt ids only. The server recomputes what each receipt still needs; this
// module merges that into the tray without duplicating anything:
//
// - each tray line remembers how many new-stock labels each receipt put there
//   (`receiptSources[receiptId]`), so opening the same link again (refresh,
//   second click, back button) replaces that receipt's share instead of
//   adding it twice;
// - new stock never exceeds what the item still needs overall, so a receipt
//   that is also selected from "Needs labels" is not counted twice either;
// - whatever else is in the tray stays untouched.

import { SELECTION_ORIGINS } from "./printSelectionModel";

const clamp = (value, max) => Math.max(0, Math.min(Number(max) || 0, Number(value) || 0));

/**
 * Plans the tray changes for a `printing/label-demand/receipts` response.
 * Returns the line updates plus a per-receipt report for the message shown.
 */
export const planReceiptMerge = (selection, result) => {
  const entries = selection?.entries || {};
  const itemsByKey = new Map((result?.items || []).map((item) => [item.key, item]));
  const contributions = new Map();

  (result?.receipts || []).forEach((receipt) => {
    (receipt.lines || []).forEach((line) => {
      if (!itemsByKey.has(line.key)) return;
      const byReceipt = contributions.get(line.key) || {};
      byReceipt[receipt.id] = Math.max(0, Number(line.outstanding || 0));
      contributions.set(line.key, byReceipt);
    });
  });

  const updates = [];
  let added = 0;
  contributions.forEach((byReceipt, key) => {
    const item = itemsByKey.get(key);
    const existing = entries[key];
    const sources = { ...(existing?.receiptSources || {}) };
    let newStock = Number(existing?.newStock || 0);
    Object.entries(byReceipt).forEach(([receiptId, quantity]) => {
      newStock = newStock - Number(sources[receiptId] || 0) + quantity;
      sources[receiptId] = quantity;
    });
    newStock = clamp(newStock, item.demand?.outstanding);
    const before = Number(existing?.newStock || 0);
    if (!existing && newStock <= 0) return;
    added += Math.max(0, newStock - before);
    updates.push({
      key,
      item,
      isNew: !existing,
      newStock,
      extra: Number(existing?.extra || 0),
      receiptSources: sources,
      origin: existing?.origin || SELECTION_ORIGINS.NEEDS_LABELS,
    });
  });

  const otherLabels = Object.entries(entries)
    .filter(([key]) => !contributions.has(key))
    .reduce((sum, [, entry]) => sum + Number(entry.newStock || 0) + Number(entry.extra || 0), 0);

  return {
    updates,
    added,
    // Opened again (refresh, second click): the tray already has these labels.
    alreadyInTray: added === 0 && updates.length > 0 && updates.every((update) => !update.isNew),
    trayHadOtherLabels: otherLabels,
    receipts: (result?.receipts || []).map((receipt) => describeReceipt(receipt)),
    notFound: result?.notFound || [],
  };
};

/** What happened to a receipt's labels, for the message after opening the link. */
export const describeReceipt = (receipt) => {
  const required = Number(receipt.labelsRequired || 0);
  const outstanding = Number(receipt.labelsOutstanding || 0);
  let status = "all";
  if (!receipt.needsLabels) status = "not_needed";
  else if (outstanding <= 0) status = "nothing_left";
  else if (outstanding < required) status = "partial";
  return {
    id: receipt.id,
    kind: receipt.kind,
    productTitle: receipt.product?.title || "",
    status,
    required,
    outstanding,
    settled: Number(receipt.labelsSettled || 0),
    dismissed: Number(receipt.labelsDismissed || 0),
    pending: Number(receipt.labelsPending || 0),
    pendingJobs: receipt.pendingJobs || [],
  };
};
